"use client";

import { c as _c } from "react/compiler-runtime";
import { IconX } from "@tabler/icons-react";
import { useSetAtom } from "jotai";
import Link from "next/link";
import { useCallback, useMemo, useRef } from "react";
import { Favicon } from "@/components/icons/favicon";
import { HomeSearchSuggestionsSkeleton } from "@/components/search/home-search-suggestions-skeleton";
import { useAnalytics } from "@/lib/analytics/client";
import { pendingDomainAtom } from "@/lib/atoms/search-atoms";
import { useSearchHistory } from "@/lib/stores/search-history-store";
import { MAX_HISTORY_ITEMS } from "@domainstack/constants";
import { Button } from "@domainstack/ui/button";
import { ScrollArea } from "@domainstack/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";
import { cn } from "@domainstack/ui/utils";
export type HomeSearchSuggestionsClientProps = {
  defaultSuggestions: string[];
  className?: string;
  faviconSize?: number;
  max?: number;
};
export function HomeSearchSuggestionsClient(t0) {
  const $ = _c(34);
  const {
    defaultSuggestions,
    className,
    max: t1
  } = t0;
  const max = t1 === undefined ? MAX_HISTORY_ITEMS : t1;
  const analytics = useAnalytics();
  const setPendingDomain = useSetAtom(pendingDomainAtom);
  const scrollContainerRef = useRef(null);
  const {
    history,
    clearHistory,
    hydrated
  } = useSearchHistory();
  let t2;
  if ($[0] !== history) {
    t2 = new Set(history);
    $[0] = history;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const historySet = t2;
  let t3;
  if ($[2] !== defaultSuggestions || $[3] !== history || $[4] !== historySet) {
    let t4;
    if ($[6] !== historySet) {
      t4 = d => !historySet.has(d);
      $[6] = historySet;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    t3 = [...history, ...defaultSuggestions.filter(t4)];
    $[2] = defaultSuggestions;
    $[3] = history;
    $[4] = historySet;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const merged = t3;
  let t4;
  if ($[8] !== max || $[9] !== merged) {
    t4 = merged.slice(0, max);
    $[8] = max;
    $[9] = merged;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const displayedSuggestions = t4;
  let t5;
  if ($[11] !== analytics || $[12] !== setPendingDomain) {
    t5 = domain => {
      analytics.track("search_suggestion_clicked", {
        domain,
        source: "suggestion"
      });
      setPendingDomain(domain);
    };
    $[11] = analytics;
    $[12] = setPendingDomain;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleClick = t5;
  let t6;
  if ($[14] !== analytics || $[15] !== clearHistory) {
    t6 = () => {
      clearHistory();
      analytics.track("search_history_cleared");
      if (scrollContainerRef.current && typeof scrollContainerRef.current.scrollTo === "function") {
        scrollContainerRef.current.scrollTo({
          left: 0,
          behavior: "smooth"
        });
      }
    };
    $[14] = analytics;
    $[15] = clearHistory;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleClearHistory = t6;
  if (!hydrated) {
    let t7;
    if ($[17] !== className || $[18] !== displayedSuggestions.length) {
      t7 = displayedSuggestions.length > 0 ? <HomeSearchSuggestionsSkeleton className={className} count={displayedSuggestions.length} /> : null;
      $[17] = className;
      $[18] = displayedSuggestions.length;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    return t7;
  }
  let t7;
  if ($[20] !== className) {
    t7 = cn("w-full [&_[data-slot=scroll-area-viewport]]:overscroll-y-none", className);
    $[20] = className;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== displayedSuggestions || $[23] !== handleClick) {
    t8 = displayedSuggestions.map(domain_0 => <Button key={domain_0} variant="secondary" size="sm" className="shrink-0 gap-2 border-none bg-muted/40 px-2.5 leading-none ring-1 ring-ring/20 hover:bg-muted/60" onClick={e => {
      e.preventDefault();
      handleClick(domain_0);
    }} nativeButton={false} aria-label={domain_0} render={<Link href={`/${encodeURIComponent(domain_0)}`}><Favicon domain={domain_0} className="shrink-0" />{domain_0}</Link>} />);
    $[22] = displayedSuggestions;
    $[23] = handleClick;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== handleClearHistory || $[26] !== history.length) {
    t9 = history.length > 0 ? <Tooltip><TooltipTrigger render={<Button variant="ghost" size="icon-sm" onClick={handleClearHistory} className="shrink-0" aria-label="Clear history"><IconX /><span className="sr-only">Clear history</span></Button>} /><TooltipContent>Clear history</TooltipContent></Tooltip> : <div className="-ml-2 w-[1px] shrink-0" />;
    $[25] = handleClearHistory;
    $[26] = history.length;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = <div className="flex gap-2 p-0.5">{t8}{t9}</div>;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t7) {
    t11 = <ScrollArea className={t7} scrollRef={scrollContainerRef} hideScrollbar={true}>{t10}</ScrollArea>;
    $[31] = t10;
    $[32] = t7;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
}